import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState, useCallback } from "react";
import Player from "@/components/LottiePlayer";
import SocialProof from "../ckan/SocialProof";
import Hero from "../ckan/Herov2";
import FaqList from "components/blog/Faq";

/* ───────────────────────── data ───────────────────────── */

const painPoints = [
  {
    num: "01",
    title: "Manual dashboards, every month",
    text: "Someone on your team still builds the report by hand. Every single month.",
  },
  {
    num: "02",
    title: "Data living in silos",
    text: "Finance in Xero, web traffic in Analytics, CRM elsewhere. They never talk to each other.",
  },
  {
    num: "03",
    title: "Spreadsheet purgatory",
    text: "Copy. Paste. Format. Repeat. In the age of AI, your team is still doing this.",
  },
  {
    num: "04",
    title: "Software that almost works",
    text: "Two days of research, a credit card entered, and it doesn't quite fit your situation.",
  },
];

const howItWorksSteps = [
  {
    num: 1,
    day: "Day 1",
    title: "Define the question",
    text: "Identify the single highest-value business question and audit which data sources hold the relevant data.",
    tag: "Discovery",
    active: true,
  },
  {
    num: 2,
    day: "Days 2–4",
    title: "Connect and wrangle data",
    text: "Ingest 1–2 data sources, normalise formats, and handle messy real-world data. No clean schema prerequisite.",
    tag: "Data layer",
    active: true,
  },
  {
    num: 3,
    day: "Days 5–7",
    title: "Build the interface",
    text: "Chat-style query layer built on top of your data. Ask in plain English and get structured answers with supporting data.",
    tag: "Build",
    active: true,
  },
  {
    num: 4,
    day: "Days 8–10",
    title: "Validate and deliver",
    text: "Demo against your real data, validate answer accuracy, and hand over a written implementation roadmap for next steps.",
    tag: "Delivery",
    active: true,
  },
  {
    num: 5,
    day: "Week 2+",
    title: "Scale — optional",
    text: "Move into an Automation Sprint to eliminate manual workflows, then an ongoing AI Data Ops Partnership for continuous development.",
    tag: "Scale",
    active: false,
  },
];

const jtbdCards = [
  {
    num: "01",
    title: "Urgent decision support from fragmented data",
    scenario:
      'Finance in Xero, CRM in HubSpot, ops in spreadsheets. No unified view. Can\'t answer "what\'s our actual margin this month?" without 2 hours of manual work.',
    outcome:
      "Single chat interface returning accurate answers from all three sources in seconds.",
  },
  {
    num: "02",
    title: "Eliminate recurring manual reporting",
    scenario:
      "Team member spends 4–6 hours monthly compiling a management report from 3 data sources.",
    outcome:
      "Automated pipeline that generates the report on schedule with zero manual input.",
  },
  {
    num: "03",
    title: "Bespoke tool when off-the-shelf doesn't fit",
    scenario:
      "Evaluated multiple BI tools. All require clean schemas, long setup, or don't support the specific queries needed.",
    outcome:
      "Custom-built interface tailored to exact query types, data formats, and team workflow.",
  },
  {
    num: "04",
    title: "Unified decision layer across siloed systems",
    scenario:
      "Sales, finance, and ops each use different tools with no shared data layer. Cross-functional questions require emailing multiple departments.",
    outcome:
      "Integrated query layer across all systems returning consistent, trusted answers.",
  },
  {
    num: "05",
    title: "Scale prototype to production infrastructure",
    scenario:
      "Prototype is working and trusted. Now needs reliability hardening, expanded data sources, and rollout to additional teams.",
    outcome:
      "Production-grade data operations with ongoing development support.",
  },
];

const offerTiers = [
  {
    stage: 1,
    title: "AI Data Answer Prototype",
    badge: "10 Days",
    tagline:
      "The front-door offer. Prove value before any long commitment.",
    description:
      "We connect 1–2 of your existing data sources and ship a working chat-style interface built around your specific business question. Real answers from your real data — by day 10.",
    ticks: [
      "Working prototype delivered in 10 days",
      "Real answers from your actual data",
      "Clear implementation roadmap for what comes next",
    ],
    cta: { label: "Start a 10-Day Prototype →", href: "/contact" },
    primary: true,
  },
  {
    stage: 2,
    title: "Automation Sprint",
    badge: "2–4 Weeks",
    tagline:
      "After prototype validation, eliminate the manual work around it.",
    description:
      "The monthly report still built by hand. The reconciliation that takes a day. The data flow nobody has ever automated. We go after your highest-friction recurring workflows and remove them.",
    ticks: [
      "Reporting and reconciliation automated end-to-end",
      "Cross-system data flows connected",
      "Fewer errors, less effort, lower ops cost",
    ],
    cta: { label: "Automate My Workflow →", href: "/contact" },
    primary: false,
  },
  {
    stage: 3,
    title: "AI Data Ops Partnership",
    badge: "Ongoing",
    tagline:
      "What starts as one prototype becomes the data layer your whole team relies on.",
    description:
      "We harden, scale, and expand what works — adding new workflows and decision tools across teams as your needs grow. Compounding value, not one-off project output.",
    ticks: [
      "Reliability and performance hardening",
      "New workflows and decision use cases",
      "Continuous ROI from your data operations",
    ],
    cta: { label: "Scale With Datopian →", href: "/contact" },
    primary: false,
  },
];

const products = [
  {
    abbr: "QL",
    name: "Queryless",
    tag: "Chat-First Data Portal",
    tagline:
      "Governed data answers, delivered inside the chat apps your team already uses.",
    description:
      "Most data products assume users will come to them. Queryless flips that. It's an agentic data portal that delivers governed answers directly in Telegram — summaries, tables, and charts — without anyone opening a BI tool. Analysts stay in control of metric definitions. Everyone else gets fast, trusted answers where they already work.",
    ticks: [
      "Ask questions in Telegram — no dashboard login needed",
      "Analyst-governed metric definitions and oversight built in",
      "Shareable report views for deeper analysis on demand",
    ],
    primaryCta: { label: "Try Queryless →", href: "https://t.me/queryless_bot" },
    secondaryCta: {
      label: "Read more",
      href: "https://www.datopian.com/blog/agentic-data-portals-chat-first",
    },
  },
  {
    abbr: "OC",
    name: "OpenClaw Autopilot",
    tag: "Personal AI Agent",
    tagline:
      "Your AI, in the apps you already use — and it remembers everything.",
    description:
      "Most AI assistants reset after every session. Autopilot is built around persistent memory: it stores context across conversations so it gets more useful every week, not smarter for an hour then forgotten. It lives in Telegram and WhatsApp, runs on isolated cloud infrastructure, and supports multiple model providers — so you stay in control.",
    ticks: [
      "Persistent memory across all conversations",
      "Works in Telegram and WhatsApp — no new app to install",
      "Multi-model, user-configured, secure isolated runtime",
    ],
    primaryCta: { label: "Try Autopilot →", href: "https://autopilot.datopian.com" },
    secondaryCta: {
      label: "Read more",
      href: "https://www.datopian.com/blog/introducing-openclaw-autopilot",
    },
  },
];

const chatExchanges = [
  {
    q: "Where are we overspending this month?",
    a: "You're over in 3 areas: SaaS (+$4,200), contractor fees (+$6,800), and cloud hosting (+$1,100). 6 tools haven't been used in 60+ days.",
  },
  {
    q: "Which channels are driving revenue?",
    a: "Organic search drives 61% of closed deals. Referral converts at 34% but is currently under-invested.",
  },
  {
    q: "What should we change to improve margin?",
    a: "Cancel 6 unused tools (saves $2,100/mo), shift 20% of paid budget to referral, and renegotiate Project Atlas contractors — 22% above market.",
  },
];

const logoBar = [
  { name: "Data.gov", src: "/assets/images/logos/socialproof/data.gov_-300x71.webp" },
  { name: "FedEx", src: "/assets/images/logos/socialproof/rsz_fedex-300x169.webp" },
  { name: "The World Bank Group", src: "/assets/images/logos/socialproof/The_World_Bank_Group-300x63.webp" },
  { name: "NHS", src: "/assets/images/logos/socialproof/NHS.webp" },
  { name: "OECD", src: "/assets/images/logos/socialproof/OECD-300x76.webp" },
];

/* ───────────────────── sub-components ────────────────── */

function SectionLabel({ children }) {
  return (
    <span className="text-orange-400 font-bold text-sm tracking-[0.14em] uppercase block mb-1">
      {children}
    </span>
  );
}

function Divider() {
  return <div className="w-9 h-[3px] bg-orange-400 rounded-sm mb-5" />;
}

function Tick({ children }) {
  return (
    <p className="relative pl-6 text-base text-secgray font-['Inter']">
      <span className="absolute left-0 text-orange-400 font-bold">✓</span>
      {children}
    </p>
  );
}

/* ───────────────────────── Chat ───────────────────────── */

function ChatWindow() {
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const sectionRef = useRef(null);
  const startedRef = useRef(false);

  const showEl = useCallback((el, delay) => {
    return new Promise((res) => {
      setTimeout(() => {
        if (el) {
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
        }
        setTimeout(res, 380);
      }, delay);
    });
  }, []);

  const typeInput = useCallback((text) => {
    return new Promise((res) => {
      const input = inputRef.current;
      if (!input) { res(); return; }
      input.value = "";
      let i = 0;
      const t = setInterval(() => {
        input.value += text[i++];
        if (i >= text.length) {
          clearInterval(t);
          setTimeout(res, 300);
        }
      }, 38);
    });
  }, []);

  const runLoop = useCallback(
    async (idx) => {
      const wrap = messagesRef.current;
      const input = inputRef.current;
      if (!wrap || !input) return;

      const ex = chatExchanges[idx % chatExchanges.length];

      // type question
      await typeInput(ex.q);
      input.value = "";

      // user bubble
      const qDiv = document.createElement("div");
      qDiv.className = "flex items-end gap-2.5 flex-row-reverse";
      qDiv.style.cssText =
        "opacity:0;transform:translateY(6px);transition:opacity 0.35s ease,transform 0.35s ease;";
      qDiv.innerHTML = `<div class="w-7 h-7 rounded-full bg-orange-400 text-white text-[0.6rem] font-bold flex items-center justify-center flex-shrink-0">You</div><div class="max-w-[80%] px-3.5 py-2.5 rounded-2xl rounded-br-sm bg-orange-400 text-white text-[0.82rem] leading-relaxed">${ex.q}</div>`;
      wrap.appendChild(qDiv);
      await showEl(qDiv, 0);

      // typing indicator
      const typingDiv = document.createElement("div");
      typingDiv.className = "flex items-end gap-2.5";
      typingDiv.style.cssText =
        "opacity:0;transform:translateY(6px);transition:opacity 0.35s ease,transform 0.35s ease;";
      typingDiv.innerHTML = `<div class="w-7 h-7 rounded-full bg-gray-200 text-gray-500 text-[0.6rem] font-bold flex items-center justify-center flex-shrink-0">AI</div><div class="inline-flex gap-1.5 items-center px-4 py-3 bg-gray-100 rounded-2xl rounded-bl-sm"><span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span><span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay:0.2s"></span><span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style="animation-delay:0.4s"></span></div>`;
      wrap.appendChild(typingDiv);
      await showEl(typingDiv, 100);
      await new Promise((r) => setTimeout(r, 1100));
      wrap.removeChild(typingDiv);

      // AI answer
      const aDiv = document.createElement("div");
      aDiv.className = "flex items-end gap-2.5";
      aDiv.style.cssText =
        "opacity:0;transform:translateY(6px);transition:opacity 0.35s ease,transform 0.35s ease;";
      aDiv.innerHTML = `<div class="w-7 h-7 rounded-full bg-gray-200 text-gray-500 text-[0.6rem] font-bold flex items-center justify-center flex-shrink-0">AI</div><div class="max-w-[80%] px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-gray-100 text-gray-700 text-[0.82rem] leading-relaxed">${ex.a}</div>`;
      wrap.appendChild(aDiv);
      await showEl(aDiv, 0);

      await new Promise((r) => setTimeout(r, 2200));

      // fade out
      [qDiv, aDiv].forEach((el) => {
        el.style.opacity = "0";
        el.style.transform = "translateY(-6px)";
      });
      await new Promise((r) => setTimeout(r, 400));
      if (wrap.contains(qDiv)) wrap.removeChild(qDiv);
      if (wrap.contains(aDiv)) wrap.removeChild(aDiv);
      await new Promise((r) => setTimeout(r, 400));

      runLoop(idx + 1);
    },
    [showEl, typeInput]
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            runLoop(0);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [runLoop]);

  return (
    <div ref={sectionRef} className="flex-shrink-0 w-full lg:w-[320px]">
      {/* Device frame */}
      <div className="relative rounded-[2.5rem] bg-gradient-to-b from-[#e8e8e8] to-[#d4d4d4] p-[6px]">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          <div className="h-7 w-36 rounded-b-2xl bg-[#1a1a1a] flex items-center justify-center">
            <div className="w-16 h-[5px] rounded-full bg-[#333]" />
          </div>
        </div>

        {/* Screen */}
        <div className="relative rounded-[2.1rem] bg-white overflow-hidden flex flex-col h-[500px]">
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-9 pb-2">
            <span className="text-gray-800 text-[0.65rem] font-semibold">9:41</span>
            <div className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5 text-gray-800" fill="currentColor" viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
              <svg className="w-3.5 h-3.5 text-gray-800" fill="currentColor" viewBox="0 0 24 24"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
            </div>
          </div>

          {/* App header */}
          <div className="px-5 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm leading-none tracking-tight">
                  Data Assistant
                </p>
                <p className="text-emerald-500 mt-1 text-[0.7rem] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                  Connected to your data
                </p>
              </div>
            </div>
          </div>

          {/* Suggestion chips */}
          <div className="flex flex-wrap gap-2 px-5 py-3">
            <span className="bg-gray-50 text-gray-500 text-[0.72rem] px-3 py-1.5 rounded-full border border-gray-200">
              Where are we overspending?
            </span>
            <span className="bg-gray-50 text-gray-500 text-[0.72rem] px-3 py-1.5 rounded-full border border-gray-200">
              Revenue by channel?
            </span>
            <span className="bg-gray-50 text-gray-500 text-[0.72rem] px-3 py-1.5 rounded-full border border-gray-200">
              Improve margin?
            </span>
          </div>

          {/* Messages area */}
          <div
            ref={messagesRef}
            className="flex-1 flex flex-col gap-3 justify-end px-5 pb-3 overflow-hidden"
          />

          {/* Input bar */}
          <div className="px-4 pb-5 pt-2">
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask your data…"
                readOnly
                className="bg-transparent border-none outline-none flex-1 text-[0.82rem] text-gray-800 font-['Inter'] min-w-0 placeholder-gray-400"
              />
              <button
                className="bg-orange-400 hover:bg-orange-500 border-none rounded-xl w-8 h-8 flex items-center justify-center flex-shrink-0 transition-colors"
                aria-label="Send"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>

          {/* Home indicator */}
          <div className="flex justify-center pb-2">
            <div className="w-28 h-1 rounded-full bg-gray-300" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Accordion ────────────────────── */

function AccordionItem({ item }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`cursor-pointer px-4 sm:px-6 bg-white transition-colors hover:bg-gray-50 border-b border-gray-100 last:border-b-0`}
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center gap-3 py-4 select-none">
        <span className="text-xs font-extrabold text-orange-400 w-6 flex-shrink-0">
          {item.num}
        </span>
        <span
          className={`text-base font-semibold flex-1 transition-colors ${
            open ? "text-orange-400" : "text-maingray"
          }`}
        >
          {item.title}
        </span>
        <div
          className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
            open ? "bg-orange-400 rotate-180" : "bg-gray-100"
          }`}
        >
          <svg
            viewBox="0 0 10 6"
            fill="none"
            stroke={open ? "#fff" : "#94a3b8"}
            strokeWidth="1.5"
            className="w-2.5 h-2.5"
          >
            <path d="M1 1l4 4 4-4" />
          </svg>
        </div>
      </div>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? "max-h-80" : "max-h-0"
        }`}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pl-9 pb-5">
          <div>
            <span className="text-[0.7rem] font-bold tracking-widest uppercase text-slate-400 block mb-1">
              Scenario
            </span>
            <p className="text-sm text-gray-600 leading-relaxed">
              {item.scenario}
            </p>
          </div>
          <div>
            <span className="text-[0.7rem] font-bold tracking-widest uppercase text-orange-400 block mb-1">
              Outcome
            </span>
            <p className="text-sm text-gray-600 leading-relaxed">
              {item.outcome}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ MAIN PAGE ═══════════════════════ */

export default function AIDataServices() {
  return (
    <div className="bg-homeExpert bg-no-repeat bypass-filter bg-engineeringPosition font-['Inter'] !overflow-x-hidden">
      {/* ① HERO */}
      <section>
        <Hero
          topTitle="AI Data Services"
          title="Turn your scattered business data into answers you can act on."
          subtitle="Most businesses are sitting on data they can't use. We connect your existing sources to a working AI interface in 10 days — no migration, no new tools, no data team required."
        />
      </section>

      <section className="pt-8">
        <SocialProof />
      </section>

      {/* ② THE PROBLEM */}
      <section className="py-20 px-6 bg-[#F7F8F9]">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>The Problem</SectionLabel>
          <Divider />
          <h2 className="medium-title mb-4">
            Still stitching spreadsheets in 2026?
          </h2>
          <p className="text-secgray mb-10 leading-relaxed text-lg">
            Most teams already have the data they need. The problem is it lives
            in disconnected systems, manual workflows, and monthly reporting
            routines that take too long.
          </p>

          {/* pain grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 bg-gray-200 gap-px rounded-2xl overflow-hidden">
            {painPoints.map((p) => (
              <div
                key={p.num}
                className="bg-white p-7 relative hover:bg-gray-50 transition-colors"
              >
                <span className="absolute top-5 right-5 text-3xl font-extrabold text-gray-100 leading-none select-none">
                  {p.num}
                </span>
                <p className="font-semibold text-maingray mb-2 text-base">
                  {p.title}
                </p>
                <p className="text-secgray text-base leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ③ IMAGINE INSTEAD */}
      <section className="px-6 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
            {/* left copy */}
            <div className="flex flex-col flex-1 min-w-0">
              <SectionLabel>Imagine Instead</SectionLabel>
              <Divider />
              <h2 className="medium-title mb-3">
                What would you ask if you could just… ask?
              </h2>
              <p className="text-secgray text-lg leading-relaxed mb-4 max-w-xl">
                We build interfaces that let your team get trusted answers from your
                own data, in plain language — without touching a spreadsheet.
              </p>
              <p className="text-secgray leading-relaxed mb-4 text-lg">
                Your data already holds the answers — it&apos;s just scattered
                across tools and never connected. We sit on top of your existing
                systems so your team can simply ask.
              </p>
              <p className="text-secgray leading-relaxed mb-6 text-lg">
                No new software to learn. No analyst required. Just answers, fast.
              </p>
              <Link
                href="/contact"
                className="btn-primary self-start"
              >
                I want answers like these →
              </Link>
            </div>

            {/* right chat */}
            <div className="relative flex-shrink-0 w-full lg:w-[320px] lg:translate-y-16">
              <ChatWindow />
            </div>
          </div>
        </div>
      </section>

      {/* ④ HOW IT WORKS */}
      <section className="py-20 px-6 bg-[#F7F8F9]">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>How It Works</SectionLabel>
          <Divider />
          <h2 className="medium-title mb-4">
            From your data to working answers in 10 days.
          </h2>
          <p className="text-secgray mb-12 text-lg leading-relaxed">
            A fixed, repeatable process. No open-ended discovery. No scope
            creep.
          </p>

          <div className="relative pl-10">
            {/* vertical line */}
            <div
              className="absolute left-[11px] top-2 bottom-2 w-px"
              style={{
                background:
                  "linear-gradient(to bottom, #fb923c, #fdba74, #e2e8f0)",
              }}
            />

            {howItWorksSteps.map((step, i) => (
              <div
                key={step.num}
                className={`relative ${
                  i < howItWorksSteps.length - 1 ? "mb-8" : ""
                }`}
              >
                {/* circle */}
                <div
                  className={`absolute -left-10 top-[2px] w-6 h-6 rounded-full flex items-center justify-center text-[0.65rem] font-extrabold flex-shrink-0 ${
                    step.active
                      ? "bg-orange-400 text-white"
                      : "bg-gray-200 text-gray-400"
                  }`}
                >
                  {step.num}
                </div>

                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <span
                    className={`text-[0.7rem] font-bold tracking-widest uppercase whitespace-nowrap ${
                      step.active ? "text-orange-400" : "text-gray-400"
                    }`}
                  >
                    {step.day}
                  </span>
                  <span className="font-bold text-maingray text-base">
                    {step.title}
                  </span>
                  <span
                    className={`inline-block text-[0.65rem] font-semibold px-2 py-0.5 rounded-full ${
                      step.active
                        ? "bg-orange-50 text-orange-400"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {step.tag}
                  </span>
                </div>

                <p className="text-secgray text-base leading-relaxed">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ⑤ WHAT WE GET DONE (JTBD) */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>What We Get Done</SectionLabel>
          <Divider />
          <h2 className="medium-title mb-3">
            We work backwards from your specific problem.
          </h2>
          <p className="text-secgray mb-12 text-lg leading-relaxed">
            Every engagement is built around a real business question. Click any
            scenario to see the outcome.
          </p>

          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            {jtbdCards.map((item) => (
              <AccordionItem key={item.num} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* ⑥ THE OFFER */}
      <section className="py-20 px-6 bg-[#F7F8F9]">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>The Offer</SectionLabel>
          <Divider />
          <h2 className="medium-title mb-4">
            Three stages. Value before commitment.
          </h2>
          <p className="text-secgray mb-12 text-lg leading-relaxed max-w-2xl">
            Start with a prototype. Automate what works. Scale what matters. Most
            clients begin at stage one and grow from there.
          </p>

          <div className="flex flex-col gap-5">
            {offerTiers.map((tier) => (
              <div
                key={tier.stage}
                className="bg-white border border-gray-200 rounded-xl p-6 transition-shadow hover:shadow-lg hover:border-orange-200"
              >
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-orange-400 text-white flex items-center justify-center font-extrabold text-sm flex-shrink-0">
                    {tier.stage}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-maingray">
                        {tier.title}
                      </h3>
                      <span className="bg-orange-50 text-orange-400 text-[0.75rem] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                        {tier.badge}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-3 font-medium">
                      {tier.tagline}
                    </p>
                    <p className="text-secgray text-base leading-relaxed mb-4">
                      {tier.description}
                    </p>
                    <div className="flex flex-col gap-1.5 mb-5">
                      {tier.ticks.map((t) => (
                        <Tick key={t}>{t}</Tick>
                      ))}
                    </div>
                    <Link
                      href={tier.cta.href}
                      className={tier.primary ? "btn-primary" : "btn-secondary"}
                    >
                      {tier.cta.label}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ⑦ OUR PRODUCTS */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>Our Products</SectionLabel>
          <Divider />
          <h2 className="medium-title mb-3">
            AI-first tools built from what we&apos;ve learned.
          </h2>
          <p className="text-secgray mb-12 text-lg leading-relaxed max-w-2xl">
            Alongside our bespoke services, we build and ship products that put
            AI where decisions actually happen — not behind another login.
          </p>

          <div className="flex flex-col gap-5">
            {products.map((prod) => (
              <div
                key={prod.abbr}
                className="bg-white border border-gray-200 rounded-xl p-6 transition-shadow hover:shadow-lg hover:border-orange-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-400 flex items-center justify-center font-extrabold text-xs flex-shrink-0">
                    {prod.abbr}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-maingray">
                        {prod.name}
                      </h3>
                      <span className="inline-block bg-orange-50 text-orange-400 text-[0.7rem] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                        {prod.tag}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-3 font-medium">
                      {prod.tagline}
                    </p>
                    <p className="text-secgray text-base leading-relaxed mb-4">
                      {prod.description}
                    </p>
                    <div className="flex flex-col gap-1.5 mb-5">
                      {prod.ticks.map((t) => (
                        <Tick key={t}>{t}</Tick>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={prod.primaryCta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary"
                      >
                        {prod.primaryCta.label}
                      </a>
                      <a
                        href={prod.secondaryCta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary"
                      >
                        {prod.secondaryCta.label}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ⑧ WHY DATOPIAN */}
      <section className="py-20 px-6 bg-[#F7F8F9]">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>Why Datopian</SectionLabel>
          <Divider />
          <h2 className="medium-title mb-6">
            World-scale data expertise.
            <br />
            Applied to your business.
          </h2>
          <p className="text-secgray leading-relaxed mb-3 text-lg">
            We have spent nearly two decades solving data problems at a scale
            most firms never encounter — building data infrastructure for
            national governments, the World Bank, NHS, and Fortune 500 companies.
          </p>
          <p className="text-secgray leading-relaxed mb-10 text-lg">
            That depth of expertise is what we bring to your data. With modern AI
            tooling, we can show you results in days — not the months a project
            of this complexity once required.
          </p>

          {/* Logo bar */}
          <div className="py-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-6">
              {logoBar.map((logo) => (
                <div
                  key={logo.name}
                  className="opacity-40 grayscale h-7 flex items-center"
                >
                  <Image
                    src={logo.src}
                    alt={logo.name}
                    width={100}
                    height={28}
                    className="max-w-fit w-auto h-7 object-contain bypass-filter"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="relative bg-white rounded-2xl p-8 border border-gray-200">
            <div className="absolute top-3 right-6 text-[7rem] leading-none text-gray-100 font-serif select-none pointer-events-none">
              &ldquo;
            </div>
            <p className="relative text-gray-800 leading-relaxed mb-8 text-lg font-serif italic max-w-[90%]">
              The agility, technical prowess, and steadfast dedication your team
              demonstrated were truly commendable. Your efforts were integral to
              our successes.
            </p>
            <div className="w-8 h-0.5 bg-orange-400 mb-4 rounded-sm" />
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-white tracking-wide">
                SK
              </div>
              <div>
                <p className="font-semibold text-maingray text-base leading-none mb-1">
                  Shailesh Kumar
                </p>
                <p className="text-gray-400 text-sm">
                  Analytics and Data Architect, Scottish &amp; Southern Electricity
                  Networks
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ⑨ CTA */}
      <section className="bg-white py-20 relative w-screen max-w-screen overflow-hidden" tabIndex={0}>
        <h3 className="text-center mx-auto medium-title font-['Inter'] text-maingray px-8">
          Start with one business question.
        </h3>
        <p className="text-center font-['Inter'] text-maingray mt-4 px-8 sm:px-0 mx-auto xl:max-w-4xl lg:max-w-3xl md:max-w-2xl max-w-sm">
          Tell us the one thing you most need to understand about your business
          right now. We&apos;ll show you a working answer system — built from
          your own data — in ten days.
        </p>
        <div className="flex flex-col sm:flex-row mt-8 gap-y-8 sm:gap-x-8 w-full justify-center px-8 sm:px-0 mx-auto xl:max-w-4xl lg:max-w-3xl md:max-w-2xl max-w-sm">
          <div className="btn-primary">
            <Link href="/contact">Start a 10-Day Prototype</Link>
          </div>
          <div className="btn-primary">
            <Link href="https://calendar.app.google/LT4acVdKn3Cxm2MXA" target="_blank">
              Schedule a Call
            </Link>
          </div>
        </div>
      </section>

      {/* ⑩ FAQ */}
      <section className="py-20 px-6 bg-[#F7F8F9]">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>FAQ</SectionLabel>
          <Divider />
          <h2 className="medium-title mb-10">
            Common questions.
          </h2>
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <FaqList faqName="ai-data-services" variant="page" />
          </div>
        </div>
      </section>
    </div>
  );
}
